import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { toast } from "react-toastify";
import { auth } from "../firebase";
import {
  LuLayoutDashboard, LuClipboardList, LuGift, LuHistory, LuUserRound,
  LuLogOut, LuLifeBuoy, LuMenu, LuX, LuChevronRight, LuSparkles,
} from "react-icons/lu";

const navItems = [
  { to: "/dashboard", label: "Overview", icon: LuLayoutDashboard },
  { to: "/surveys", label: "Surveys", icon: LuClipboardList },
  { to: "/rewards", label: "Rewards", icon: LuGift },
  { to: "/history", label: "History", icon: LuHistory },
  { to: "/profile", label: "My profile", icon: LuUserRound },
];

export function PageHeading({ eyebrow, title, description, action }) {
  return <div className="page-heading">
    <div><div className="eyebrow">{eyebrow}</div><h1>{title}</h1>{description && <p>{description}</p>}</div>
    {action && <div className="heading-action">{action}</div>}
  </div>;
}

export function StatCard({ label, value, detail, icon: Icon, tone = "green" }) {
  return <article className={`stat-card tone-${tone}`}>
    <div className="stat-top"><span className="stat-icon"><Icon size={20} strokeWidth={1.9} /></span><span className="stat-label">{label}</span></div>
    <div className="stat-value">{value}</div><div className="stat-detail">{detail}</div>
  </article>;
}

export function LoadingState({ label = "Loading your panel…" }) {
  return <div className="state-card"><span className="spinner"/><strong>{label}</strong><span>Just a moment while we get things ready.</span></div>;
}

export function EmptyState({ icon: Icon = LuSparkles, title, description, action }) {
  return <div className="state-card empty-state"><span className="empty-icon"><Icon size={25}/></span><strong>{title}</strong><span>{description}</span>{action}</div>;
}

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();
  const user = auth.currentUser;
  const initials = (user?.displayName || user?.email || "Member").split(/[ @._-]/).filter(Boolean).slice(0, 2).map(x => x[0]).join("").toUpperCase();
  const logout = async () => {
    setLoggingOut(true);
    try { await signOut(auth); toast.success("You have been signed out."); navigate("/login", { replace: true }); }
    catch (error) { toast.error(error?.message || "Unable to sign out. Please try again."); }
    finally { setLoggingOut(false); setLogoutOpen(false); }
  };
  return <div className="app-shell">
    <header className="topbar">
      <button className="mobile-menu icon-button" aria-label={mobileOpen ? "Close navigation" : "Open navigation"} onClick={() => setMobileOpen(v => !v)}>{mobileOpen ? <LuX/> : <LuMenu/>}</button>
      <NavLink to="/dashboard" className="brand-lockup" aria-label="Teqluc Research dashboard"><img src={require("../logo.png")} alt="Teqluc Research"/><span className="brand-tagline">Your opinions. Better decisions.</span></NavLink>
      <div className="topbar-right"><a className="help-link" href="mailto:support@teqluc.com"><LuLifeBuoy size={17}/> <span>Help & support</span></a><span className="topbar-divider"/><button className="member-menu" onClick={() => navigate("/profile")}><span className="avatar">{initials || "M"}</span><span className="member-name">{user?.displayName || user?.email?.split("@")[0] || "Member"}<small>Panel member</small></span><LuChevronRight size={16}/></button></div>
    </header>
    <div className="shell-body">
      <aside className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`}>
        <div className="nav-caption">YOUR PANEL</div>
        <nav className="side-nav" aria-label="Member navigation">{navItems.map(({to,label,icon:Icon}) => <NavLink key={to} to={to} onClick={() => setMobileOpen(false)} className={({isActive}) => `side-link ${isActive ? "active" : ""}`}><Icon size={19} strokeWidth={1.8}/><span>{label}</span>{label === "Surveys" && <span className="nav-arrow"><LuChevronRight size={15}/></span>}</NavLink>)}</nav>
        <div className="sidebar-spacer"/>
        <div className="support-card"><span className="support-icon"><LuLifeBuoy size={19}/></span><strong>Need a hand?</strong><p>Our team is here to help you.</p><a href="mailto:support@teqluc.com">Get support <LuChevronRight size={14}/></a></div>
        <button className="side-link signout-link" onClick={() => setLogoutOpen(true)}><LuLogOut size={19}/><span>Sign out</span></button>
        <div className="sidebar-foot">© {new Date().getFullYear()} Teqluc Research</div>
      </aside>
      {mobileOpen && <button className="sidebar-backdrop" aria-label="Close navigation" onClick={() => setMobileOpen(false)}/>}
      <main className="main-content">{children}</main>
    </div>
    {logoutOpen && <div className="modal-backdrop" role="presentation" onMouseDown={e => { if(e.target===e.currentTarget) setLogoutOpen(false); }}><section className="modal-card" role="dialog" aria-modal="true" aria-labelledby="logout-title"><span className="modal-icon"><LuLogOut size={22}/></span><h2 id="logout-title">Sign out of your panel?</h2><p>You can sign back in any time to continue where you left off.</p><div className="modal-actions"><button className="btn btn-quiet" onClick={() => setLogoutOpen(false)} disabled={loggingOut}>Stay signed in</button><button className="btn btn-primary" onClick={logout} disabled={loggingOut}>{loggingOut ? "Signing out…" : "Sign out"}</button></div></section></div>}
  </div>;
}
